// Preact hook for subscribing components to the current hash route.
// Per spec/tech-arch.md §6 + spec/build-plan.md P-023 row.
//
// **Subscribe pattern.** `useState`'s lazy initializer parses the
// current hash on mount; `useEffect` attaches a `hashchange` listener
// and returns the cleanup on unmount. happy-dom does not fire
// `hashchange` on programmatic `window.location.hash = ...` writes,
// so tests dispatch the synthetic event after mutating the hash; real
// browsers fire it natively.
//
// **Why no in-effect re-sync (diverges from D-082 useStore).** useStore
// runs `setState(store.getState())` inside its effect to cover the
// render → effect race window — and that re-sync is referentially
// safe because `store.getState()` returns the same object identity
// when state has not changed. `parseHash(...)` creates a fresh Route
// object on every call, so an in-effect re-sync here would always
// fire a structurally-identical-but-referentially-new setState,
// wasting a render per mount. `window.location.hash` only mutates
// via `hashchange` events anyway (the browser fires the event after
// any URL-bar / link / programmatic update), so the listener already
// covers every real change.
//
// **Read-only.** This hook does not mutate `window.location.hash`.
// The `unknown`-route redirect (per spec/tech-arch.md §6 + the P-023
// build-plan QA item) lives in `<RouterOutlet />` (src/app/routes.tsx)
// via a useEffect — keeping the hook itself a pure parse-and-
// subscribe surface lets multiple consumers share the listener without
// each one fighting over the URL bar.

import { useEffect, useState } from 'preact/hooks'

import { parseHash, type Route } from '../routes'

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash))
  useEffect(() => {
    const handle = () => setRoute(parseHash(window.location.hash))
    window.addEventListener('hashchange', handle)
    return () => window.removeEventListener('hashchange', handle)
  }, [])
  return route
}
